import type { WebsiteSuggestion } from "./types";

/**
 * Pure URL + suggestion helpers for organization website research.
 * The network call itself lives in lib/api.ts.
 */

export function normalizeWebsiteSuggestions(suggestions: WebsiteSuggestion[], organization: string) {
  const seen = new Set<string>();
  return suggestions
    .map((suggestion) => {
      const url = normalizeDisplayUrl(suggestion.url);
      const domain = suggestion.domain || safeDomain(url);
      return {
        title: cleanInlineText(suggestion.title) || organization,
        url,
        domain,
        description: cleanInlineText(suggestion.description),
        evidence: cleanInlineText(suggestion.evidence),
        reason: cleanInlineText(suggestion.reason),
        sourceName: cleanInlineText(suggestion.sourceName),
        sourceUrl: normalizeDisplayUrl(suggestion.sourceUrl || ""),
        confidence: clampConfidence(suggestion.confidence)
      };
    })
    .filter((suggestion) => {
      if (!suggestion.url || !suggestion.domain) return false;
      const key = normalizeComparableUrl(suggestion.url);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 4);
}

export function createWebsiteFallback(organization: string): WebsiteSuggestion[] {
  const base = organization
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
  const shortened = base
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/\b(the|foundation|fund|trust|inc|llc|ltd|limited|group|partners?)\b/g, " ")
    .trim();
  const words = base.split(/\s+/).filter(Boolean);
  const trailingDescriptor = ["trust", "fund"].includes(words[words.length - 1] || "");
  const compactBase = base.replace(/\s+/g, "");
  const compactShort = shortened.replace(/\s+/g, "");
  const hyphenBase = base.replace(/\s+/g, "-");
  const hyphenShort = shortened.replace(/\s+/g, "-");
  const candidates = Array.from(new Set(
    (trailingDescriptor
      ? [compactShort, compactBase, hyphenShort, hyphenBase]
      : [compactBase, compactShort, hyphenBase, hyphenShort]
    ).filter(Boolean)
  ));
  return candidates.slice(0, 2).map((candidate, index) => {
    const url = `https://${candidate}.org`;
    return {
      title: `${organization} official website candidate`,
      url,
      domain: safeDomain(url),
      description: "Conservative generated candidate. Confirm before final use.",
      evidence: "Local fallback generated from the organization name because live research was unavailable.",
      confidence: index === 0 ? 0.44 : 0.36,
      reason: "Use only if this matches the known official website."
    };
  });
}

export function normalizeDisplayUrl(value: string) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  try {
    const url = new URL(raw.startsWith("http") ? raw : `https://${raw}`);
    url.hash = "";
    return url.toString().replace(/\/$/, "");
  } catch {
    return "";
  }
}

export function normalizeComparableUrl(value: string) {
  const url = normalizeDisplayUrl(value);
  if (!url) return "";
  try {
    const parsed = new URL(url);
    return `${parsed.hostname.replace(/^www\./, "")}${parsed.pathname.replace(/\/$/, "")}`.toLowerCase();
  } catch {
    return url.toLowerCase();
  }
}

export function safeDomain(value: string) {
  try {
    return new URL(normalizeDisplayUrl(value)).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

export function clampConfidence(value: number) {
  const confidence = Number(value);
  if (!Number.isFinite(confidence)) return 0.5;
  return Math.max(0.05, Math.min(0.99, confidence));
}

export function cleanInlineText(value: string | undefined) {
  return String(value || "")
    .replace(/\s*\[\d+(?:,\s*\d+)*\]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}
